import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  api,
  type AdminOrgEnriched,
  type AdminSubdomainsListRow,
} from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// Joined view of `/api/admin/organizations/enriched` and
// `/api/admin/subdomains`. Lets staff see "which orgs have a subdomain,
// which don't" without paging through every org one at a time.
type SubdomainAdminRow = AdminOrgEnriched & {
  subdomain: string | null;
  subdomainUpdatedAt: string | null;
};

export function SubdomainAdminPage() {
  useDocumentTitle("Admin: Subdomains");
  const [selected, setSelected] = useState<SubdomainAdminRow | null>(null);

  const { data: orgsResponse, isLoading: orgsLoading, isError: orgsError, error: orgsErr } = useQuery({
    queryKey: ["admin-organizations-enriched"],
    queryFn: api.admin.orgsEnriched,
  });

  const { data: subsResponse, isLoading: subsLoading, isError: subsError, error: subsErr } = useQuery({
    queryKey: ["admin-subdomains"],
    queryFn: api.admin.subdomains.list,
  });

  const rows = useMemo<SubdomainAdminRow[]>(() => {
    const orgs = orgsResponse?.data ?? [];
    const subs = new Map<string, AdminSubdomainsListRow>(
      (subsResponse?.data ?? []).map((s) => [s.organizationId, s]),
    );
    return orgs.map((o) => ({
      ...o,
      subdomain: subs.get(o.id)?.subdomain ?? null,
      subdomainUpdatedAt: subs.get(o.id)?.updatedAt ?? null,
    }));
  }, [orgsResponse, subsResponse]);

  const columns: ColumnDef<SubdomainAdminRow>[] = useMemo(
    () => [
      { accessorKey: "name", header: "Organization" },
      {
        accessorKey: "subdomain",
        header: "Brain URL",
        cell: ({ row }) => {
          const sub = row.original.subdomain;
          return sub ? (
            <code className="font-mono text-xs">brain.{sub}.boolab.io</code>
          ) : (
            <span className="text-muted-foreground">unassigned</span>
          );
        },
      },
      {
        accessorKey: "containerCount",
        header: "Containers",
        cell: ({ getValue }) => (
          <span className="tabular-nums">{getValue() as number}</span>
        ),
      },
      {
        accessorKey: "subdomainUpdatedAt",
        header: "Last changed",
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            formatDate(v)
          ) : (
            <span className="text-muted-foreground">{"\u2014"}</span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Subdomains"
        subtitle="Manage org subdomains under boolab.io. Renames reprovision the org's brain containers' custom domains; SSL takes 1–3 minutes."
      />

      {orgsLoading || subsLoading ? (
        <LoadingIndicator />
      ) : orgsError || !orgsResponse ? (
        <ErrorComponent error={orgsErr ?? "Failed to load organizations"} />
      ) : subsError ? (
        <ErrorComponent error={subsErr ?? "Failed to load subdomains"} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          isLoading={false}
          onRowClick={(row) => setSelected(row)}
        />
      )}

      <SubdomainEditSheet
        row={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function SubdomainEditSheet({
  row,
  onClose,
}: {
  row: SubdomainAdminRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const orgId = row?.id;

  const detail = useQuery({
    queryKey: ["admin-subdomain", orgId],
    queryFn: () => api.admin.subdomains.get(orgId!),
    enabled: !!orgId,
  });

  // Seed the input from the current subdomain whenever a new row opens.
  // Done as a derived state pattern — controlled <Input> with reset on
  // orgId change. Keeps this small without a useEffect.
  const currentSubdomain = detail.data?.data.subdomain ?? "";
  if (orgId && draft === "" && detail.data && currentSubdomain && draft !== currentSubdomain) {
    setDraft(currentSubdomain);
  }

  const update = useMutation({
    mutationFn: (subdomain: string) =>
      api.admin.subdomains.update(orgId!, subdomain),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subdomains"] });
      queryClient.invalidateQueries({ queryKey: ["admin-subdomain", orgId] });
      setConfirmOpen(false);
    },
  });

  const close = () => {
    setDraft("");
    setConfirmOpen(false);
    onClose();
  };

  const trimmed = draft.trim().toLowerCase();
  const dirty = trimmed.length > 0 && trimmed !== currentSubdomain;

  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && close()}>
      <SheetContent className="data-[side=right]:sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{row?.name ?? "Organization"}</SheetTitle>
        </SheetHeader>

        {detail.isLoading ? (
          <LoadingIndicator />
        ) : detail.isError || !detail.data ? (
          <ErrorComponent error={detail.error ?? "Failed to load org subdomain"} />
        ) : (
          <div className="px-4 pb-4 space-y-6">
            <section className="space-y-2">
              <Label htmlFor="subdomain-edit">Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="subdomain-edit"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="acme"
                  className="font-mono"
                />
                <span className="text-sm text-muted-foreground">.boolab.io</span>
              </div>
              <p className="text-xs text-muted-foreground">
                3–30 characters, lowercase letters / digits / dashes. Brain containers in this org will reprovision their public hostnames; SSL takes 1–3 minutes.
              </p>
              {update.isError && <InlineError error={update.error} />}
              <div className="pt-2">
                <Button
                  disabled={!dirty || update.isPending}
                  onClick={() => setConfirmOpen(true)}
                >
                  {update.isPending && (
                    <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                  )}
                  Save and reprovision
                </Button>
              </div>
            </section>

            <section className="space-y-2">
              <Label className="text-sm font-medium">Brain containers</Label>
              {detail.data.data.containers.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  This org has no active brain containers yet.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {detail.data.data.containers.map((c) => (
                    <li
                      key={c.id}
                      className="rounded-md border bg-muted/30 px-3 py-2 text-xs"
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">
                        {c.customDomainFqdn ?? "(no custom domain yet)"}
                      </div>
                      {c.customDomainStatus && (
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          status: {c.customDomainStatus}
                          {c.customDomainError && ` — ${c.customDomainError}`}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}

        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={`Rename to ${trimmed}.boolab.io?`}
          description="Tears down and recreates the custom domain for every brain container in this org. The default .up.railway.app URL keeps working throughout."
          confirmLabel="Rename"
          loading={update.isPending}
          onConfirm={() => update.mutate(trimmed)}
        />
      </SheetContent>
    </Sheet>
  );
}
